// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '../../lib/base';
import { AddCircleOutlineIcon } from '../../lib/icons';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  EditOrderInputsDataQuery,
  EditOrderInputsDataQueryVariables,
  EditOrderInputsMutation,
  EditOrderInputsMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, getTimeRanges } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { ExitDialogButton } from '../common/button';
import DialogInfoTitle from '../common/dailog/DailogInfoTitle';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import NewCropOrBlendToUseForm, {
  type Props as NewCropOrBlendToUseFormProps,
  type CropData,
  type BlendData,
  type CropOrBlendToUseFormInput,
} from './NewCropOrBlendToUseForm';
import OrderInputsChangeTable from './OrderInputsChangeTable';
import { getOrderChanges } from './getOrderChanges';
import { getTraysFromCropsAndBlends } from './getTrayCounts';
import {
  filterParseSortBlendData,
  filterParseSortCropData,
  inputsToNewCropOrBlendToUseFormProps,
  preprareFormsForSubmit,
} from './orderFormUtils';
import { createSizeKey } from './sizeUtils';

const EDIT_ORDER_INPUTS_MUT = gql`
  mutation EditOrderInputs(
    $order_id: Int!
    $use_crops: [CreateOrderUseCropsInput!]!
    $use_blends: [CreateOrderUseBlendsInput!]!
  ) {
    editOrderInputs(
      input: {
        order_id: $order_id
        use_crops: $use_crops
        use_blends: $use_blends
      }
    ) {
      updated_order {
        id
        updated_at
      }
      updated_plantings {
        id
        planting_state
        trays_rounded
        order_id
        updated_at
      }
      updated_tasks {
        id
        order_id
        updated_at
      }
      deleted_tasks {
        id
      }
      deleted_plantings {
        id
      }
      deleted_inputs {
        id
      }
      plantings_created {
        id
      }
      inputs_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;
const EDIT_ORDER_INPUTS_DATA_QUERY = gql`
  query EditOrderInputsData(
    $id: ID!
    $orderTime: Int!
    $todayStart: Int!
  ) {
    order(id: $id) {
      id
      order_status
      order_fulfillment_time
      plantings {
        edges {
          node {
            id
            planting_status
            trays_rounded
            crop {
              id
              crop_name
              yield_unit
              total_grow_time
              harvest_buffer_time
            }
          }
        }
      }
      inputs {
        edges {
          node {
            id
            size_amount
            quantity
            unit
            price_per_item
            product_id
            crop {
              id
              crop_name
              total_grow_time
              harvest_buffer_time
              yield_unit
              yield_per_tray
              buffer_percentage
              status
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
            }
            blend {
              id
              blend_name
              status
              edge_sizes {
                edges {
                  node {
                    id
                    size
                    unit
                    price_per_item
                  }
                }
              }
              crops {
                edges {
                  node {
                    id
                    percent_of_blend
                    crop {
                      id
                      crop_name
                      total_grow_time
                      harvest_buffer_time
                      yield_unit
                      yield_per_tray
                      buffer_percentage
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    crops(
      filters: [{ name: "crop.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "crop.crop_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          harvest_buffer_time
          yield_per_tray
          yield_unit
          buffer_percentage
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
        }
      }
    }
    blends(
      filters: [{ name: "blend.status", operator: "!=", value: "archived" }]
      orderBy: [{ name: "blend.blend_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          blend_name
          status
          edge_sizes {
            edges {
              node {
                id
                size
                unit
                price_per_item
              }
            }
          }
          crops {
            edges {
              node {
                id
                percent_of_blend
                crop {
                  id
                  crop_name
                  total_grow_time
                  harvest_buffer_time
                  yield_per_tray
                  yield_unit
                  buffer_percentage
                }
              }
            }
          }
        }
      }
    }
    surpluses(
      timeAfter: $todayStart
      timeBefore: $orderTime
    ) {
      edges {
        node {
          plantings {
            id
            trays_rounded
            planting_time
            crop {
              id
              max_hold_time
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly orderId: number;
  readonly orderTime: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditOrderInputsDialog({ orderId, orderTime, isOpen, onClose, onSuccess }: Props) {
  const { timezone, preferredWeightUnit } = useUserContext();
  const { todayStart, todayStartDate, sevenDaysFromTodayStart } = getTimeRanges(timezone);
  const apolloClient = useApolloClient();
  const [getOrderData, { data: orderData, loading: orderDataLoading, error: orderDataError }] = useLazyQuery<
    EditOrderInputsDataQuery,
    EditOrderInputsDataQueryVariables
  >(EDIT_ORDER_INPUTS_DATA_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [editOrderInputs, { loading: editOrderInputsLoading }] = useMutation<
    EditOrderInputsMutation,
    EditOrderInputsMutationVariables
  >(EDIT_ORDER_INPUTS_MUT);

  const [filteredCropsData, setFilteredCropsData] = React.useState<CropData[]>([]);
  const [blendsData, setBlendsData] = React.useState<BlendData[]>([]);
  const [orderPlantingData, setOrderPlantingData] = React.useState<
    NonNullable<EditOrderInputsDataQuery['order']>['plantings']['edges'][number]['node'][]
  >([]);

  const orderTimeEndOfDay = dayjs.unix(orderTime).tz(timezone).endOf('day').unix();
  React.useEffect(() => {
    if (isOpen) {
      void getOrderData({
        variables: { id: createID.order(orderId), orderTime: orderTimeEndOfDay, todayStart },
        onCompleted: (orderDataIn) => {
          if (orderDataIn?.order != null && orderDataIn?.crops != null) {
            const readyFilteredCropsData = filterParseSortCropData(orderDataIn.crops);
            setFilteredCropsData(readyFilteredCropsData);

            const readyBlendsData = filterParseSortBlendData(orderDataIn.blends);
            setBlendsData(readyBlendsData);

            const inputs = orderDataIn.order.inputs.edges.map(({ node }) => node);
            const { cropFormValues, blendFormValues } = inputsToNewCropOrBlendToUseFormProps({
              orderInputs: inputs,
              preferredWeightUnit,
            });
            setCropOrBlendForms(
              cropFormValues.concat(blendFormValues).map((values, idx) => {
                const formKey = utils.getRandomInt();
                return (
                  <NewCropOrBlendToUseForm
                    key={formKey}
                    formKey={formKey}
                    index={idx}
                    disableFields={disableFields}
                    values={values}
                    onChangeField={handleCropOrBlendFormInputChange}
                    onRemove={removeCropOrBlendForm}
                    cropsData={readyFilteredCropsData}
                    blendsData={readyBlendsData}
                  />
                );
              }),
            );
            const orderPlantings = orderDataIn.order.plantings.edges.map(({ node }) => node);
            setOrderPlantingData(orderPlantings);
            // setOrderFulfillmentTime(dayjs.unix(orderDataIn.order.order_fulfillment_time).tz(timezone));
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(`Problem getting order data: ${errorIn.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, orderId, orderTimeEndOfDay, todayStart, getOrderData, preferredWeightUnit]);

  const disableFields = orderDataLoading || editOrderInputsLoading;

  const [cropOrBlendForms, setCropOrBlendForms] = React.useState<React.ReactElement<NewCropOrBlendToUseFormProps>[]>(
    [],
  );

  const addCropOrBlendToUseForm = () => {
    setCropOrBlendForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        ...prevForms,
        <NewCropOrBlendToUseForm
          key={formKey}
          formKey={formKey}
          index={prevForms.length}
          disableFields={disableFields}
          values={{
            cropOrBlendSelected: undefined,
            itemCount: '',
            harvestTime: dayjs(),
            itemCountValidation: '',
            countUnit: createSizeKey({
              size: null,
              unit: preferredWeightUnit,
              pricePerItem: null,
              productId: null,
            }),
            pricePerItem: '',
            pricePerItemValidation: '',
          }}
          onChangeField={handleCropOrBlendFormInputChange}
          onRemove={removeCropOrBlendForm}
          cropsData={filteredCropsData}
          blendsData={blendsData}
          // onEnter={onEnter}
        />,
      ];
    });
  };
  const removeCropOrBlendForm = (index: number) => {
    setCropOrBlendForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms.splice(index, 1);
      return updatedForms.map((old, idx) => React.cloneElement(old, { index: idx }));
    });
  };
  const handleCropOrBlendFormInputChange = (
    index: number,
    fieldName: keyof CropOrBlendToUseFormInput,
    value: string | number | null | undefined | CropData | BlendData,
    validationFieldName?: string,
  ) => {
    setCropOrBlendForms((prevForms) => {
      const updatedForms = [...prevForms];
      // If fieldName is 'cropSelected', then check if the countUnit exists in the possible options
      // for the newly selected crop. If not then reset the countUnit to the default
      const existingFormValues: CropOrBlendToUseFormInput = updatedForms[index].props.values;
      let newCountUnit = existingFormValues.countUnit;
      if (fieldName === 'cropOrBlendSelected') {
        const newValue = value as CropData;
        const existingCountUnit = existingFormValues.countUnit;
        const possibleSizeKeys = newValue.sizes.map((size) =>
          createSizeKey({
            size: size.size,
            unit: size.unit,
            pricePerItem: size.price_per_item ?? null,
            productId: size.product_id ?? null,
          }),
        );
        if (!possibleSizeKeys.includes(existingCountUnit)) {
          newCountUnit = createSizeKey({
            size: null,
            unit: preferredWeightUnit,
            pricePerItem: null,
            productId: null,
          });
        }
      }
      const newValues: CropOrBlendToUseFormInput = {
        ...updatedForms[index].props.values,
        countUnit: newCountUnit,
        // This must come after countUnit is set
        // In case the countUnit is the value being changed
        [fieldName]: value,
      };
      if (validationFieldName !== undefined && value !== '') {
        // @ts-ignore
        newValues[validationFieldName] = '';
      }
      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      return updatedForms;
    });
  };

  const onSubmit = () => {
    const { cropsToUse, blendsToUse } = preprareFormsForSubmit(cropOrBlendForms);
    void editOrderInputs({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: DASHBOARD_OVERDUE_TASKS_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            first: 25,
          },
        },
        {
          query: SURPLUSES_BUFFERS_QUERY,
          variables: {
            timeAfter: todayStart,
          },
        },
      ],
      update: (cache, { data }) => {
        const plantingsCreated = (data?.editOrderInputs?.plantings_created?.length ?? 0) > 0;
        const plantingsDeleted = (data?.editOrderInputs?.deleted_plantings?.length ?? 0) > 0;
        const tasksCreated = (data?.editOrderInputs?.tasks_created?.length ?? 0) > 0;
        const tasksDeleted = (data?.editOrderInputs?.deleted_tasks?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantings(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksDeleted || tasksCreated ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated || tasksDeleted ? DELETE : existingTasks;
            },
            taskGroup(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            plantingGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
            planting_groups(existingGroup, { DELETE }) {
              return plantingsCreated || plantingsDeleted ? DELETE : existingGroup;
            },
            plantingTimeGroup(existingGroup, { DELETE }) {
              return plantingsCreated ? DELETE : existingGroup;
            },
          },
        });
      },
      variables: {
        order_id: orderId,
        use_crops: cropsToUse,
        use_blends: blendsToUse,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editOrderInputs != null) {
          const updatedOrderData = dataIn.editOrderInputs;
          enqueueSnackbar('Updated order contents', { variant: 'success' });
          setCropOrBlendForms([]);
          evictCacheData({
            apolloClient,
            orderInputIds: updatedOrderData.deleted_inputs.map((input) => input.id),
            taskIds: updatedOrderData.deleted_tasks.map((task) => task.id),
            plantingIds: updatedOrderData.deleted_plantings.map((planting) => planting.id),
          });
          onSuccess(true);
        } else {
          enqueueSnackbar('Error editing order', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const hasInputValidationError = cropOrBlendForms.some(({ props }) => props.values.itemCountValidation !== '');
  const requiredFieldsFilled: boolean =
    cropOrBlendForms.length > 0 &&
    cropOrBlendForms.every(
      ({
        props: {
          values: { cropOrBlendSelected, itemCount },
        },
      }) => cropOrBlendSelected !== undefined && itemCount !== '' && Number(itemCount) !== 0,
    );
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  // const onEnter = (_event: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (!isSubmitDisabled) {
  //     onSubmit();
  //   }
  // };

  const traysFromForms = getTraysFromCropsAndBlends(cropOrBlendForms);
  const changes = getOrderChanges({
    traysFromForms,
    orderPlantingData,
    surplusPlantings: orderData?.surpluses.edges.flatMap(({ node }) => node.plantings) ?? [],
    orderFulfillmentTime: orderData?.order?.order_fulfillment_time
      ? dayjs.unix(orderData.order.order_fulfillment_time).tz(timezone)
      : null,
    todayStartDate,
  });

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={1}>
      {cropOrBlendForms.map((cropOrBlendForm) => (
        <Box key={cropOrBlendForm.props.formKey}>{cropOrBlendForm}</Box>
      ))}
      <Box>
        <Grid container display="flex" flexDirection="row" alignItems="center">
          <Button
            variant="outlined"
            arial-label="add-crop-or-blend"
            onClick={addCropOrBlendToUseForm}
            disabled={editOrderInputsLoading}
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Crop/Blend
          </Button>
          <Grid flexGrow="1" />
        </Grid>
      </Box>
      <Box>
        <DialogInfoTitle>Changes</DialogInfoTitle>
        <OrderInputsChangeTable changes={changes} />
      </Box>
    </Box>
  );

  const dialogContent = orderDataError ? (
    <ErrorView error={orderDataError} />
  ) : orderDataLoading || orderData?.order == null ? (
    <PageLoading />
  ) : (
    form
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Order Contents</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
